import React, { useState, useRef } from 'react'
import { HiOutlineLogin, HiOutlineLogout, HiOutlineCheckCircle, HiOutlineXCircle } from 'react-icons/hi'
import KioskHardwareScanInput from './KioskHardwareScanInput'
import { submitKioskAttendance } from '../../../services/KioskAttendanceService'

const Transaction = {
    TIME_IN: 1,
    TIME_OUT: 2
}

// How long the result banner stays up before clearing itself. It does not
// pause scanning — the hidden input keeps listening the whole time.
const RESULT_DISPLAY_MS = 4000

const KioskAttendanceActions = () => {
    // Sticky: once a mode is chosen it stays active across scans. Clicking the
    // other button switches mode directly; nothing needs to be "stopped" first.
    const [activeTransaction, setActiveTransaction] = useState(null)
    const [submitting, setSubmitting] = useState(false)
    const [result, setResult] = useState(null) // { success: boolean, message: string }
    const resetTimer = useRef(null)
    const activeTransactionRef = useRef(null)
    const submittingRef = useRef(false)

    const handleSelectMode = (transaction) => {
        clearTimeout(resetTimer.current)
        setResult(null)
        activeTransactionRef.current = transaction
        setActiveTransaction(transaction)
    }

    const handleScan = async (qrcode) => {
        // Ignore a scan that arrives while the previous one is still submitting,
        // rather than firing a second request for the same/next employee at once.
        if (submittingRef.current) {
            return
        }
        submittingRef.current = true
        setSubmitting(true)
        try {
            const response = await submitKioskAttendance({ transaction: activeTransactionRef.current, qrcode })
            if (response.error) {
                setResult({ success: false, message: response.error })
            } else {
                setResult({ success: true, message: response.message || 'Attendance recorded.' })
            }
        } finally {
            submittingRef.current = false
            setSubmitting(false)
            resetTimer.current = setTimeout(() => setResult(null), RESULT_DISPLAY_MS)
        }
    }

    const isClockInActive = activeTransaction === Transaction.TIME_IN
    const isClockOutActive = activeTransaction === Transaction.TIME_OUT

    return (
        <div className="flex flex-col items-center">
            <div className="h-10 flex items-center mb-4">
                {result && (
                    <div
                        className={`flex items-center gap-2 px-4 py-1.5 rounded-full text-sm font-medium ${
                            result.success ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
                        }`}
                    >
                        {result.success ? (
                            <HiOutlineCheckCircle className="text-base" />
                        ) : (
                            <HiOutlineXCircle className="text-base" />
                        )}
                        {result.message}
                    </div>
                )}
                {!result && submitting && <p className="text-sm text-gray-400 animate-pulse">Recording...</p>}
            </div>

            <div className="flex gap-3">
                <button
                    onClick={() => handleSelectMode(Transaction.TIME_IN)}
                    className={`flex items-center justify-center gap-2 px-8 py-4 w-44 rounded-2xl text-base font-medium transition-colors ${
                        isClockInActive
                            ? 'bg-red-600 text-white hover:bg-red-700'
                            : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                    }`}
                >
                    <HiOutlineLogin className="text-lg" />
                    Clock In
                </button>
                <button
                    onClick={() => handleSelectMode(Transaction.TIME_OUT)}
                    className={`flex items-center justify-center gap-2 px-8 py-4 w-44 rounded-2xl text-base font-medium transition-colors ${
                        isClockOutActive
                            ? 'bg-red-600 text-white hover:bg-red-700'
                            : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                    }`}
                >
                    <HiOutlineLogout className="text-lg" />
                    Clock Out
                </button>
            </div>

            <KioskHardwareScanInput active={activeTransaction !== null} onScan={handleScan} />
        </div>
    )
}

export default KioskAttendanceActions
